import React, { memo } from 'react'
import { ISelectOption, Select } from 'components/forms/Selects'
import { DayOfWeek, ITimeRange } from 'types/datetime'
import { ICarrierCapacity } from 'apps/marketplace/store/contractLanes/types'
import { DIFFERENT, PICKUP_TIME, SAME } from 'apps/marketplace/utils/constants/rates'
import { pickupDayChecked } from 'apps/marketplace/utils/helpers/rates'
import { ChangeRateData } from 'apps/marketplace/utils/helpers/useCarrier'
import { Checkbox, Time } from 'components/forms'
import { capitalize } from 'utils/capitalize'

interface IPickupPreferenceProps {
  pickupTimeType: string
  pickupTimeEveryday?: ITimeRange
  pickupTimePreferences: ITimeRange[]
  carrierCapacity?: ICarrierCapacity
  onChange: (arg: Partial<ChangeRateData>) => void
}

export const PickupPreference = memo<IPickupPreferenceProps>(
  ({
    pickupTimeType,
    pickupTimePreferences,
    pickupTimeEveryday = { startTime: '', endTime: '' },
    carrierCapacity,
    onChange,
  }) => {
    const onPickupDayChecked = ({
      target: { checked, id },
    }: React.ChangeEvent<HTMLInputElement>) => {
      const day = id.split('-').reverse()[0] as DayOfWeek
      const preference = pickupTimePreferences.find(({ dayOfWeek }) => dayOfWeek === day)
      if (preference) {
        preference.checked = checked
        onChange({ pickupTimePreferences })
      }
    }
    const onChangePickupTimeType = ({ value }: ISelectOption<React.ReactText>) => {
      onChange({ pickupTimeType: `${value}` })
    }
    const onChangeEverydayPickupTime = (value: string, key: string) => {
      const timeRangeKey = key as keyof ITimeRange
      onChange({
        pickupTimeEveryday: {
          ...pickupTimeEveryday,
          [timeRangeKey]: value,
        },
      })
    }
    const onChangeDifferentDayPickupTime = (value: string, key: string) => {
      const [type, day] = key.split('-')
      const preference = pickupTimePreferences.find(({ dayOfWeek }) => dayOfWeek === day)
      if (preference) {
        if (type === 'startTime') {
          preference.startTime = value
        } else if (type === 'endTime') {
          preference.endTime = value
        }
        onChange({ pickupTimePreferences })
      }
    }

    return (
      <>
        <h3>Pickup Settings</h3>
        <div className='row'>
          <div className='col-md-10'>
            <label htmlFor='pickup-days'>For this lane, trucks can pick up:</label>
            <div className='col-xs-7 flex justify-between pl0' id='pickup-days'>
              {pickupTimePreferences.map(({ checked, dayOfWeek }) => {
                const id = `pickup-time-${dayOfWeek}`
                return (
                  <Checkbox
                    key={id}
                    id={id}
                    dataTest={id}
                    label={capitalize(dayOfWeek.substring(0, 3))}
                    checked={pickupDayChecked(dayOfWeek, checked, carrierCapacity)}
                    onChange={onPickupDayChecked}
                  />
                )
              })}
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col-xs-6 col-md-4'>
            <Select
              id='pickup-time-type'
              label='Pickup time (optional)'
              options={PICKUP_TIME}
              value={PICKUP_TIME.find((entry: { value: string }) => entry.value === pickupTimeType)}
              onChange={onChangePickupTimeType}
              dataTest='pickup-time-type'
            />
          </div>
        </div>
        {pickupTimeType === SAME && (
          <div className='row'>
            <div className='col-xs-2'>
              <Time
                id='pickup-start-time'
                name='startTime'
                value={pickupTimeEveryday?.startTime || ''}
                onChange={onChangeEverydayPickupTime}
                dataTest='pickup-start-time'
              />
            </div>
            <div className='col-xs-2'>
              <Time
                id='pickup-end-time'
                name='endTime'
                value={pickupTimeEveryday?.endTime || ''}
                onChange={onChangeEverydayPickupTime}
                dataTest='pickup-end-time'
              />
            </div>
          </div>
        )}
        {pickupTimeType === DIFFERENT && (
          <>
            {pickupTimePreferences
              .filter(({ checked, dayOfWeek }) =>
                pickupDayChecked(dayOfWeek, checked, carrierCapacity)
              )
              .map(({ dayOfWeek, endTime, startTime }) => (
                <div className='row' key={dayOfWeek}>
                  <label className='col-xs-1 pt2' htmlFor={`pickup-start-time-${dayOfWeek}`}>
                    {capitalize(dayOfWeek.substring(0, 3))}
                  </label>
                  <div className='col-xs-2'>
                    <Time
                      id={`pickup-start-time-${dayOfWeek}`}
                      name={`startTime-${dayOfWeek}`}
                      value={startTime || ''}
                      onChange={onChangeDifferentDayPickupTime}
                      dataTest={`pickup-start-time-${dayOfWeek}`}
                    />
                  </div>
                  <div className='col-xs-2'>
                    <Time
                      id={`pickup-end-time-${dayOfWeek}`}
                      name={`endTime-${dayOfWeek}`}
                      value={endTime || ''}
                      onChange={onChangeDifferentDayPickupTime}
                      dataTest={`pickup-end-time-${dayOfWeek}`}
                    />
                  </div>
                </div>
              ))}
          </>
        )}
      </>
    )
  }
)
